import * as React from 'react';
import { Link } from 'react-router';
import styled, { keyframes, withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import { TextP } from '../../Defaults/Text';
import { FlexColCenter, FlexBetween } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { ProductPriceCart } from './ProductPriceCart';
import { TagType, Tag } from './Tag';
import { FreeDelivery } from './FreeDelivery';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { OrderNrInput } from './OrderNrInput';
import { StockInfo } from './StockInfo';
import { getZemplinProductDefaultInfo } from '../selectors';
import { SaleBubble } from '../ProductDetail/SaleBubble';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { IsInCartAlready } from '../ProductDetail/LeftUpper';

export interface Props {
  addToRequest: any;
  addToCart: any;
  product: any;
  user?: any;
  handleOrderNrChange: (goodId, value) => void;
  showPackage: boolean;
  isInCart: boolean;
  theme: any;
}

function ProductTabDefault({
  addToCart,
  addToRequest,
  product,
  user,
  handleOrderNrChange,
  showPackage,
  isInCart,
  theme,
}: Props) {
  const {
    picture,
    url,
    name,
    issaleout,
    is_new,
    issale,
    goodId,
    currency,
    issaleb2b,
  } = getProductDefaultInfo(product);
  const {
    unit,
    onStock,
    price,
    oldPrice,
    priceWithoutVat,
    orderNr: currentOrdernr,
    storesInfo,
    isSpojMat,
    productPackages,
    statusB2B,
    statusB2C,
    specialDiscountPcn,
    specialDiscount,
    specialDiscountWithoutVat,
    freeDelivery,
  } = getZemplinProductDefaultInfo(product);
  const isB2b = user && user.b2b;
  const statusShowPrice = isB2b ? statusB2B !== 3 : statusB2C !== 3;
  const statusShowAddToCart = isB2b ? statusB2B === 0 : statusB2C === 0;
  const correctPrice = statusShowPrice
    ? onStock > 0 || !isSpojMat
      ? isB2b
        ? (+priceWithoutVat).toFixed(2)
        : (+price).toFixed(2)
      : ''
    : '';
  const noStock = !statusShowAddToCart || (!isB2b ? +onStock === 0 : false);
  const correctShownSpecialDiscount =
    specialDiscountPcn && specialDiscountPcn !== 0
      ? specialDiscountPcn
      : isB2b
      ? specialDiscountWithoutVat
      : specialDiscount;
  const correctShownSpecialDiscountText =
    correctShownSpecialDiscount && correctShownSpecialDiscount !== 0
      ? specialDiscountPcn && specialDiscountPcn
        ? `-${correctShownSpecialDiscount}%`
        : `${correctShownSpecialDiscount}${currency}`
      : '';
  const isSale = isB2b ? issaleb2b : issale;

  let oldPriceCorrect =
    onStock > 0 || !isSpojMat ? (isB2b ? +priceWithoutVat : +price) : 0;
  oldPriceCorrect = correctShownSpecialDiscountText.includes('%')
    ? oldPriceCorrect / ((100 - correctShownSpecialDiscount) / 100)
    : correctShownSpecialDiscountText.includes(currency)
    ? oldPriceCorrect - +correctShownSpecialDiscount
    : 0;

  return (
    <Wrapper>
      <TopBorder />
      <RightBorder />
      <NameWrapper to={url}>
        <Name>{name}</Name>
      </NameWrapper>
      {user && (
        <OrderNrInput
          goodId={goodId}
          defaultValue={currentOrdernr}
          handleChange={handleOrderNrChange}
        />
      )}
      <TagsWrapper>
        {is_new ? <Tag type={TagType.NEW} text={'Novinka'} /> : null}
        {issaleout ? <Tag text={'Výpredaj'} type={TagType.SALEOUT} /> : null}
        {isSale ? <Tag text={'Akcia'} type={TagType.ACTION} /> : null}
        {correctShownSpecialDiscountText ? (
          <SaleBubble
            notAbsolute={true}
            text={correctShownSpecialDiscountText}
          />
        ) : null}
      </TagsWrapper>

      <MobileWrapper>
        {is_new ? <Tag type={TagType.NEW} text={'Novinka'} /> : null}
        {issaleout ? <Tag text={'Výpredaj'} type={TagType.SALEOUT} /> : null}
        {isSale ? <Tag text={'Akcia'} type={TagType.ACTION} /> : null}
        {correctShownSpecialDiscountText ? (
          <SaleBubble
            notAbsolute={true}
            text={correctShownSpecialDiscountText}
          />
        ) : null}
      </MobileWrapper>
      <Link to={url}>
        <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
      </Link>
      {freeDelivery && <FreeDelivery />}
      {isInCart && (
        <IsInCartAlready>
          {__('Produkt sa nachádza v košíku')}
          <SvgIcon
            icon={IconType.cart}
            fill={theme.colors.primary}
            marginLeft={8}
            alt={__('Produkt sa nachádza v košíku')}
            title={__('Produkt sa nachádza v košíku')}
          />
        </IsInCartAlready>
      )}
      <StockInfo
        withoutExactStockCount={false}
        onStock={onStock}
        unit={unit}
        storesInfo={storesInfo}
        productPackages={showPackage ? productPackages : ''}
      />
      <ProductPriceCart
        price={correctPrice}
        oldPrice={oldPriceCorrect ? (+oldPriceCorrect).toFixed(2) : ''}
        unit={unit}
        addToRequest={addToRequest}
        addToCart={addToCart}
        withVat={!isB2b}
        noStock={noStock}
        isInCart={isInCart}
      />
    </Wrapper>
  );
}

export const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexColCenter)`
  animation: ${animateIn} 0.3s ease;
  width: 100%;
  min-width: ${rem(250)};
  max-width: 31.5%;
  margin: 0 ${rem(24)} 0 0;
  padding: ${rem(48)} ${rem(32)} ${rem(48)} ${rem(8)};
  border-radius: ${rem(2)};
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;
  justify-content: space-between;

  &:nth-child(3n) {
    margin-right: 0;
    padding-right: ${rem(8)};

    ${({ theme }) => theme.mediab.l1300`
      margin-right: ${rem(24)};
      padding-right: ${rem(48)};
    `}

    ${({ theme }) => theme.mediab.m810`
      margin-right: 0;
      padding-right:  ${rem(8)};
    `}
  }

  &:first-child,
  &:nth-child(2),
  &:nth-child(3) {
    padding-top: 0;

    ${({ theme }) => theme.mediab.l1300`
      padding-top: ${rem(48)};
      &:first-child,
      &:nth-child(2) {
        padding-top: 0;
      }
    `}

    ${({ theme }) => theme.mediab.m810`
      padding-top: ${rem(32)};

      &:first-child,
      &:nth-child(2) {
        padding-top: ${rem(32)};
      }
    `}
  }

  &:first-child > :first-child,
  &:nth-child(2) > :first-child,
  &:nth-child(3) > :first-child,
  &:nth-child(3n) > :nth-child(2) {
    border: 0;
  }

  ${({ theme }) => theme.mediab.l1300`
    max-width: 47%;

    &:nth-child(2n) {
      margin-right: 0;
      padding-right: ${rem(8)};
    }

    &:nth-of-type(3) > :first-child {
      border-top: ${theme.borders.primary};
    }

    &:nth-child(3n) > :nth-child(2) {
      border-right: ${theme.borders.primary};
    }

    &:first-child > :first-child,
    &:nth-child(2) > :first-child,
    &:nth-child(2n) > :nth-child(2) {
      border: 0;
    }
  `}

  ${({ theme }) => theme.mediab.m810`
    width: 100%;
    max-width: initial;
    min-width: initial;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: ${rem(32)};

    & > :nth-child(2)  {
      border: 0;
    }

    &:nth-child(2n) {
      margin-right: 0;
      padding-right: 0;
    }

    &:first-child > :first-child,
    &:nth-child(2) > :first-child,
    &:nth-child(2n) > :nth-child(2) {
      border-right: 0;
      border-top: ${theme.borders.primary};
    }

    &:first-child > :nth-child(2), &:nth-child(3n) > :nth-child(2) {
      border-right: 0;
    }

    &:last-child {
      border-bottom: ${theme.borders.primary};
    }
  `}
`;

const Image = styled.img`
  width: ${rem(160)};
  height: ${rem(160)};
  margin-bottom: ${rem(41)};
  transition: transform 0.2s;

  &:hover {
    transform: scale(0.9);
  }
`;

const TopBorder = styled.div`
  border-top: ${({ theme }) => theme.borders.primary};
  position: absolute;
  left: 2%;
  right: 6%;
  top: 0;

  ${({ theme }) => theme.mediab.m810`
      left: 1%;
      right: 1%;
  `};
`;

const RightBorder = styled.div`
  border-right: ${({ theme }) => theme.borders.primary};
  position: absolute;
  bottom: 8%;
  top: 8%;
  right: 0;
`;

const NameWrapper = styled(Link)`
  width: 100%;
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(12)};
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  width: 100%;
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 400;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.m810`
      margin-bottom: ${rem(8)};
  `}
`;

const TagsWrapper = styled(FlexBetween)`
  width: 100%;
  margin: ${rem(2)} 0;

  ${({ theme }) => theme.mediab.m810`
      display: none;
  `}
`;

const SecondaryText = styled(TextP)`
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

const MobileWrapper = styled(FlexBetween)`
  display: none;
  ${({ theme }) => theme.mediab.m810`
      display: flex;
      width: 100%;
  `}
`;

const WithTheme: any = withTheme(ProductTabDefault);
export { WithTheme as ProductTabDefault };
